'use client';

import { Check, X } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { FullShareViewSiteDetails } from './FullShareViewSiteDetails';
import { NotificationBanner } from './NotificationBanner';
import { ShareLinkField } from './ShareLinkField';
import { useCopyShareLink } from './useCopyShareLink';

import type { ShareModalClientProps } from './ShareModalClient';

/**
 * Tailwind replica of WDS CustomModalLayout (title/subtitle + close, content,
 * actions row, footnote). Rendered when the bmHarmonyTheme experiment is off.
 */
export function ModalLayoutShareView({
  shareUrl,
  siteName,
  imageUrl,
  siteUrl,
  ownerUrl,
  onClose,
  showFootnote = true,
}: Omit<
  ShareModalClientProps,
  'origin' | 'commentsCount' | 'unreadCount' | 'bmHarmonyTheme'
>) {
  const t = useTranslations();
  const { copied, copyError, handleClose, handleCopyLink } = useCopyShareLink(
    shareUrl,
    onClose,
  );

  return (
    <div
      data-hook="share-lb"
      className="flex w-[600px] max-w-full flex-col overflow-hidden rounded-lg bg-white text-[#162d3d]"
      style={{
        maxHeight: 'min(627px, calc(100vh))',
        boxShadow:
          '0 0 12px 0 rgba(22,45,61,.12), 0 6px 20px 0 rgba(22,45,61,.12)',
      }}
    >
      <div className="shrink-0 px-6 pt-6">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg leading-6 font-bold">{t('share-title')}</h2>
            <p className="text-base leading-6 font-normal">
              {t('share-subtitle')}
            </p>
          </div>
          <button
            type="button"
            data-hook="share-close"
            onClick={handleClose}
            aria-label={t('share-cancel')}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-[#162d3d] hover:text-[#116dff]"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div
        data-hook="share-content"
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pt-6"
      >
        <FullShareViewSiteDetails
          siteName={siteName}
          imageUrl={imageUrl}
          siteUrl={siteUrl}
        />

        {copyError ? (
          <NotificationBanner
            variant="destructive"
            dataHook="notifications-copy-error"
            text={t('share-sendError')}
          />
        ) : null}

        <ShareLinkField shareUrl={shareUrl} label={t('share-linkLabel')} />
      </div>

      <div
        data-hook="share-actions"
        className="flex shrink-0 items-center justify-end gap-6 px-6 py-6"
      >
        <button
          type="button"
          data-hook="share-cancel-footer"
          onClick={handleClose}
          className="cursor-pointer text-sm text-[#116dff] hover:text-[#0b4fd0]"
        >
          {t('share-cancel')}
        </button>
        <button
          type="button"
          data-hook="copy-link"
          onClick={handleCopyLink}
          disabled={!shareUrl}
          className="flex min-h-9 cursor-pointer items-center gap-1 rounded-full bg-[#116dff] px-6 text-sm text-white hover:bg-[#0b4fd0] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {copied ? <Check size={14} /> : null}
          {copied ? t('share-linkCopied') : t('share-copy')}
        </button>
      </div>

      {showFootnote ? (
        <div
          data-hook="share-footnote"
          className="shrink-0 border-t border-[#dfe5eb] bg-[#eaf0f6] px-6 py-4 text-sm leading-5"
        >
          {t('share-footnoteText')}{' '}
          {ownerUrl ? (
            <button
              type="button"
              data-hook="share-footnote-cta"
              onClick={() => window.open(ownerUrl, '_blank')}
              className="cursor-pointer text-[#116dff] underline hover:opacity-80"
            >
              {t('share-viewComments')}
            </button>
          ) : (
            t('share-viewComments')
          )}
        </div>
      ) : null}
    </div>
  );
}
